(() => {
  if (customElements.get('cart-drawer')) return;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();

      const drawerContent = this.querySelector('#CartDrawer');
      if (drawerContent) {
        const hasQuantityInput = (node) => node.nodeType === 1 && (
          node.matches('input.quantity__input[name="updates[]"]')
          || node.querySelector('input.quantity__input[name="updates[]"]')
        );
        this.cartObserver = new MutationObserver((records) => {
          // Spinner, live-region and disclosure updates do not change totals.
          // Only replacement/addition/removal of quantity rows needs a rescan.
          if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some(hasQuantityInput))) {
            this.syncHeaderCartCount();
          }
        });
        this.cartObserver.observe(drawerContent, { childList: true, subtree: true });
      }
      this.syncHeaderCartCount();
    }

    getCartTriggers() {
      return [...new Set(document.querySelectorAll('[data-tiara-cart-trigger], #cart-icon-bubble'))];
    }

    setHeaderCartIconAccessibility() {
      this.getCartTriggers().forEach((cartLink) => {
        if (cartLink.dataset.cartDrawerBound === 'true') return;
        cartLink.dataset.cartDrawerBound = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.setAttribute('aria-controls', 'CartDrawer');

        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });

        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() !== 'SPACE') return;
          event.preventDefault();
          this.open(cartLink);
        });
      });
    }

    syncHeaderCartCount(cartItemCount) {
      const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
      const hasActualCount = Number.isSafeInteger(cartItemCount) && cartItemCount >= 0;
      let count = hasActualCount ? cartItemCount : 0;
      if (!hasActualCount) {
        this.querySelectorAll('#CartDrawer input.quantity__input[name="updates[]"]').forEach((input) => {
          count += Number.parseInt(input.value, 10) || 0;
        });
      }

      this.getCartTriggers().forEach((cartLink) => {
        if (!cartLink.matches('[data-tiara-cart-trigger]')) return;
        let badge = cartLink.querySelector('.tiara-p-header__cart-count');

        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'tiara-p-header__cart-count';
            cartLink.appendChild(badge);
          }
          if (badge.textContent !== String(count)) badge.textContent = String(count);
          const label = isArabic ? `السلة، ${count} منتجات` : `Cart, ${count} items`;
          if (cartLink.getAttribute('aria-label') !== label) cartLink.setAttribute('aria-label', label);
        } else {
          badge?.remove();
          const label = isArabic ? 'السلة فارغة' : 'Cart is empty';
          if (cartLink.getAttribute('aria-label') !== label) cartLink.setAttribute('aria-label', label);
        }
      });
    }

    open(triggeredBy) {
      if (this.classList.contains('active')) return;
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const container = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : this.querySelector('#CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (container && focusElement && typeof trapFocus === 'function') trapFocus(container, focusElement);
        },
        { once: true },
      );

      document.body.classList.add('overflow-hidden');
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      if (typeof onKeyUpEscape === 'function') {
        cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
      }
    }

    renderContents(parsedState) {
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.classList.remove('is-empty');
      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const sectionHtml = parsedState.sections?.[section.id];
        if (!sectionElement || !sectionHtml) return;

        const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (innerHtml !== null) sectionElement.innerHTML = innerHtml;
      });

      requestAnimationFrame(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.setHeaderCartIconAccessibility();
        this.syncHeaderCartCount();
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return element ? element.innerHTML : null;
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
      ];
    }
  }

  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || event.defaultPrevented) return;

    /*
     * Dawn's native <product-form> already owns its submit flow.
     * Leaving it alone prevents duplicate requests and keeps Shopify's
     * standard product form behaviour intact.
     */
    if (form.closest('product-form')) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    if (form.dataset.tiaraCartSubmitting === 'true') {
      event.preventDefault();
      return;
    }

    /*
     * This listener runs in the capture phase. Preventing the native submit
     * here makes Shopify's form-submit tracker ignore the original submit.
     * Shopify still observes the successful /cart/add fetch response, so one
     * customer click produces one product_added_to_cart / AddToCart event.
     */
    event.preventDefault();
    form.dataset.tiaraCartSubmitting = 'true';
    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    const wasDisabled = submitButton?.disabled || false;
    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;

    try {
      const formData = new FormData(form);
      window.TiaraBogo?.prepareFormData?.(formData);
      formData.delete('sections');
      formData.delete('sections_url');
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);

      const root = window.Shopify?.routes?.root || '/';
      const addUrl = window.routes?.cart_add_url || `${root}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
        throw new Error(
          state.description ||
            state.message ||
            (isArabic ? 'تعذر إضافة المنتج إلى السلة' : 'Unable to add this product to the cart'),
        );
      }

      try {
        cartDrawer.renderContents(state);
      } catch (renderError) {
        /*
         * The cart request has already succeeded. Never submit the form
         * again from this point because that would add a duplicate item.
         * The cart page is the safe recovery path if the drawer cannot
         * render the returned section.
         */
        console.error('[Tiara cart drawer render]', renderError);
        window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      }
    } catch (error) {
      console.error('[Tiara cart drawer]', error);
      const isArabic = document.documentElement.lang?.toLowerCase().startsWith('ar');
      const message =
        error?.message || (isArabic ? 'تعذر إضافة المنتج إلى السلة' : 'Unable to add this product to the cart');
      let errorNode = form.querySelector('[data-tiara-cart-form-error]');

      if (!errorNode) {
        errorNode = document.createElement('p');
        errorNode.dataset.tiaraCartFormError = '';
        errorNode.setAttribute('role', 'alert');
        errorNode.style.cssText = 'margin:8px 0 0;color:#a12626;font-size:12px;font-weight:700;line-height:1.5;';
        form.appendChild(errorNode);
      }

      errorNode.textContent = message;
    } finally {
      delete form.dataset.tiaraCartSubmitting;
      submitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = wasDisabled;
    }
  }, { capture: true });
})();
